import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSettings, useSetSetting } from '@/features/config/hooks/use-config';
import {
  flag,
  MATCH_WEIGHT_KEYS,
  num,
  REGISTRY,
  type AppSettingsMap,
  type SettingDef,
  type SettingGroup,
} from '@/features/config/model/app-setting';
import { errorMessageKey } from '@/shared/lib/errors';
import { Async } from '@/shared/ui/Async';
import { Button } from '@/shared/ui/Button';
import { Switch } from '@/shared/ui/Switch';
import { TextField } from '@/shared/ui/TextField';

const GROUP_ORDER: readonly { group: SettingGroup; titleKey: string }[] = [
  { group: 'auth', titleKey: 'config.groups.auth' },
  { group: 'notifications', titleKey: 'config.groups.notifications' },
  { group: 'features', titleKey: 'config.groups.features' },
  { group: 'matching', titleKey: 'config.groups.matching' },
  { group: 'sla', titleKey: 'config.groups.sla' },
];

/**
 * Admin runtime-config screen (Phase 10, Part C). Renders every
 * `REGISTRY` entry grouped by section; bools save immediately via a
 * `Switch`, numbers require an explicit per-row Save so a half-typed weight
 * never reaches the RPC. The matching group additionally flags (not blocks)
 * when the four match weights don't sum to 1.0.
 */
export function AdminSettingsRoute(): JSX.Element {
  const { t } = useTranslation();
  const settings = useAppSettings();

  return (
    <main className="page stack">
      <h1>{t('admin.settingsTitle')}</h1>
      <Async query={settings}>
        {(map) => (
          <div className="stack">
            {GROUP_ORDER.map(({ group, titleKey }) => (
              <section key={group} className="card stack">
                <h2>{t(titleKey)}</h2>
                {REGISTRY.filter((def) => def.group === group).map((def) => (
                  <SettingRow key={def.key} def={def} map={map} />
                ))}
                {group === 'matching' ? <MatchWeightSumNotice map={map} /> : null}
              </section>
            ))}
          </div>
        )}
      </Async>
    </main>
  );
}

function SettingRow({ def, map }: { def: SettingDef; map: AppSettingsMap }): JSX.Element {
  if (def.kind === 'bool') {
    return <BoolSettingRow def={def} checked={flag(map, def.key)} />;
  }
  return <NumberSettingRow def={def} value={num(map, def.key)} />;
}

function BoolSettingRow({
  def,
  checked,
}: {
  def: Extract<SettingDef, { kind: 'bool' }>;
  checked: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const setSetting = useSetSetting();

  return (
    <div className="stack" style={{ gap: 'var(--space-1)' }}>
      <Switch
        checked={checked}
        loading={setSetting.isPending}
        label={t(def.labelKey)}
        description={t(def.descriptionKey)}
        onChange={(next) => setSetting.mutate({ key: def.key, value: next })}
      />
      {setSetting.isError ? (
        <p className="field__error" role="alert">
          {t(errorMessageKey(setSetting.error))}
        </p>
      ) : null}
    </div>
  );
}

function NumberSettingRow({
  def,
  value,
}: {
  def: Extract<SettingDef, { kind: 'number' }>;
  value: number;
}): JSX.Element {
  const { t } = useTranslation();
  const setSetting = useSetSetting();
  const [draft, setDraft] = useState(String(value));
  const [validationError, setValidationError] = useState<string | null>(null);

  // Keep the field in sync with the confirmed server value (own edits are
  // local until Save; a Realtime update from elsewhere should still win).
  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const min = def.min ?? 0;
  const max = def.max ?? 1;

  function handleSave(): void {
    setValidationError(null);
    const parsed = Number(draft);
    if (!Number.isFinite(parsed) || parsed < min || parsed > max) {
      setValidationError(t('config.numberOutOfRange', { min, max }));
      return;
    }
    setSetting.mutate({ key: def.key, value: parsed });
  }

  const error =
    validationError ?? (setSetting.isError ? t(errorMessageKey(setSetting.error)) : undefined);

  return (
    <div className="stack" style={{ gap: 'var(--space-1)' }}>
      <p className="muted">{t(def.descriptionKey)}</p>
      <div className="row" style={{ alignItems: 'flex-end' }}>
        <TextField
          label={t(def.labelKey)}
          value={draft}
          ltr
          inputMode="decimal"
          error={error}
          onChange={(e) => setDraft(e.target.value)}
        />
        <Button variant="outline" loading={setSetting.isPending} onClick={handleSave}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}

/** Sums the four match.weight_* settings and warns (never blocks) when they
 * don't total 1.0 — the matching RPCs still run either way. */
function MatchWeightSumNotice({ map }: { map: AppSettingsMap }): JSX.Element {
  const { t } = useTranslation();
  const sum = MATCH_WEIGHT_KEYS.reduce((acc, key) => acc + num(map, key), 0);
  const rounded = Math.round(sum * 1000) / 1000;
  const balanced = Math.abs(sum - 1) < 0.005;

  return (
    <p className={balanced ? 'muted' : 'text-danger'} role={balanced ? undefined : 'alert'}>
      {t(balanced ? 'config.matching.weightSumOk' : 'config.matching.weightSumWarning', {
        sum: rounded,
      })}
    </p>
  );
}
